<template>
  <div class="saas-dist">
    <el-row :gutter="16">
      <el-col :xs="24" :md="9">
        <ContentWrap title="我的邀请海报">
          <div v-loading="profileLoading" class="invite-panel">
            <p class="invite-hint">
              分享下方海报二维码或链接，客户扫码后直接进入意向登记表单。成交后在「租户管理 · 新增」用同一手机号+邀请码成立。
              局域网调试时，二维码域名须为电脑局域网 IP（如 192.168.x.x），勿用 127.0.0.1。
            </p>
            <div class="invite-poster">
              <div class="invite-poster__stage">
                <img class="invite-poster__bg" :src="posterBg" alt="分销邀请海报" draggable="false" />
                <div v-if="inviteQrText" class="invite-poster__qr">
                  <Qrcode
                    class="invite-poster__qrcode"
                    :text="inviteQrText"
                    :width="200"
                    :options="{ margin: 1, color: { dark: '#111827', light: '#ffffff' } }"
                  />
                </div>
              </div>
            </div>
            <div class="invite-code-row">
              <span class="invite-code-label">邀请码</span>
              <span class="invite-code">{{ profile?.inviteCode || '—' }}</span>
            </div>
            <p v-if="inviteQrText" class="invite-link-preview">{{ inviteQrText }}</p>
            <div class="invite-actions">
              <el-button type="primary" @click="copyInvite">复制邀请码</el-button>
              <el-button @click="copyInviteLink">复制邀请链接</el-button>
            </div>
          </div>
        </ContentWrap>
      </el-col>

      <el-col :xs="24" :md="15">
        <ContentWrap title="拉取客户（仅记线索）">
          <el-alert
            class="mb-16px"
            type="info"
            :closable="false"
            title="提交后只记录线索，不会创建租户。同人同码重复提交会更新原线索。成立发生在租户管理新建租户匹配时。佣金结算未开通。"
          />
          <el-form
            ref="pullFormRef"
            v-loading="pullSaving"
            :model="pullForm"
            :rules="pullRules"
            label-width="100px"
            class="pull-form"
          >
            <el-form-item label="客户/公司名" prop="companyName">
              <el-input v-model="pullForm.companyName" maxlength="64" placeholder="客户公司名称" />
            </el-form-item>
            <el-form-item label="联系人" prop="contactName">
              <el-input v-model="pullForm.contactName" maxlength="32" placeholder="联系人姓名" />
            </el-form-item>
            <el-form-item label="联系手机" prop="contactMobile">
              <el-input v-model="pullForm.contactMobile" maxlength="20" placeholder="匹配键，开户时须一致" />
            </el-form-item>
            <el-form-item label="邀请码">
              <el-input :model-value="profile?.inviteCode" disabled />
            </el-form-item>
            <el-form-item label="备注" prop="remark">
              <el-input v-model="pullForm.remark" type="textarea" :rows="2" maxlength="200" show-word-limit />
            </el-form-item>
            <el-form-item>
              <el-button
                v-hasPermi="['system:saas-dist:pull']"
                type="primary"
                :loading="pullSaving"
                @click="submitPull"
              >
                提交拉取
              </el-button>
              <el-button @click="resetPull">重置</el-button>
            </el-form-item>
          </el-form>
        </ContentWrap>
      </el-col>
    </el-row>

    <ContentWrap v-if="canRebind" title="超管补绑（已开户未成立）">
      <el-alert
        class="mb-16px"
        type="warning"
        :closable="false"
        title="用于漏填邀请码等漏绑场景。已成立租户不可再绑（换绑属 P1）。"
      />
      <el-form
        ref="rebindFormRef"
        v-loading="rebindSaving"
        :model="rebindForm"
        :rules="rebindRules"
        label-width="100px"
        class="pull-form"
      >
        <el-form-item label="租户编号" prop="tenantId">
          <el-input-number v-model="rebindForm.tenantId" :min="1" controls-position="right" class="!w-220px" />
        </el-form-item>
        <el-form-item label="邀请码" prop="inviteCode">
          <el-input v-model="rebindForm.inviteCode" maxlength="32" placeholder="线索上的邀请码" />
        </el-form-item>
        <el-form-item label="确认手机" prop="contactMobile">
          <el-input v-model="rebindForm.contactMobile" maxlength="20" placeholder="须与线索手机一致" />
        </el-form-item>
        <el-form-item label="原因" prop="reason">
          <el-input v-model="rebindForm.reason" maxlength="80" placeholder="可选，写入审计引用" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" :loading="rebindSaving" @click="submitRebind">确认补绑</el-button>
        </el-form-item>
      </el-form>
    </ContentWrap>

    <ContentWrap title="我的客户（线索）">
      <el-form class="-mb-15px" :inline="true" :model="queryParams" label-width="68px">
        <el-form-item label="公司名">
          <el-input v-model="queryParams.companyName" clearable class="!w-180px" @keyup.enter="handleQuery" />
        </el-form-item>
        <el-form-item label="手机号">
          <el-input v-model="queryParams.contactMobile" clearable class="!w-160px" @keyup.enter="handleQuery" />
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="queryParams.status" clearable placeholder="全部" class="!w-120px">
            <el-option label="拉取" :value="0" />
            <el-option label="成立" :value="1" />
          </el-select>
        </el-form-item>
        <el-form-item v-if="canQueryAll">
          <el-checkbox v-model="queryParams.all">查看全量</el-checkbox>
        </el-form-item>
        <el-form-item>
          <el-button @click="handleQuery"><Icon icon="ep:search" class="mr-5px" />搜索</el-button>
          <el-button @click="resetQuery"><Icon icon="ep:refresh" class="mr-5px" />重置</el-button>
        </el-form-item>
      </el-form>

      <el-table v-loading="listLoading" :data="list" stripe>
        <el-table-column prop="companyName" label="客户/公司" min-width="140" />
        <el-table-column prop="contactName" label="联系人" width="100" />
        <el-table-column prop="contactMobile" label="手机号" width="130" />
        <el-table-column prop="inviteCodeUsed" label="邀请码" width="110" />
        <el-table-column label="分销用户" min-width="120">
          <template #default="{ row }">
            {{ row.inviterNickname || row.inviterUserId || '—' }}
          </template>
        </el-table-column>
        <el-table-column label="关联租户" min-width="140">
          <template #default="{ row }">
            {{ row.customerTenantName || (row.customerTenantId ? `#${row.customerTenantId}` : '—') }}
          </template>
        </el-table-column>
        <el-table-column label="关系状态" width="100">
          <template #default="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'warning'">
              {{ row.status === 1 ? '成立' : '拉取' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="pulledAt" label="拉取时间" width="170" />
        <el-table-column prop="establishedAt" label="成立时间" width="170" />
        <el-table-column prop="remark" label="备注" min-width="120" show-overflow-tooltip />
      </el-table>
      <Pagination
        :total="total"
        v-model:page="queryParams.pageNo"
        v-model:limit="queryParams.pageSize"
        @pagination="getList"
      />
    </ContentWrap>
  </div>
</template>

<script setup lang="ts">
import { Qrcode } from '@/components/Qrcode'
import * as SaasDistApi from '@/api/system/saas-dist'
import { checkPermi } from '@/utils/permission'
import { ElMessage } from 'element-plus'
import type { FormInstance, FormRules } from 'element-plus'
import posterBg from '@/assets/imgs/saas-dist/invite-poster.png'

defineOptions({ name: 'SystemSaasDist' })

const message = useMessage()
const canQueryAll = computed(() => checkPermi(['system:saas-dist:query-all']))
const canRebind = computed(() => checkPermi(['system:saas-dist:rebind']))

const profileLoading = ref(false)
const profile = ref<SaasDistApi.SaasDistProfileVO>()
const pullFormRef = ref<FormInstance>()
const pullSaving = ref(false)
const pullForm = reactive({
  companyName: '',
  contactName: '',
  contactMobile: '',
  remark: ''
})
const pullRules: FormRules = {
  companyName: [{ required: true, message: '请输入客户/公司名', trigger: 'blur' }],
  contactName: [{ required: true, message: '请输入联系人', trigger: 'blur' }],
  contactMobile: [
    { required: true, message: '请输入联系手机', trigger: 'blur' },
    { pattern: /^1\d{10}$/, message: '手机号格式不正确', trigger: 'blur' }
  ]
}

const rebindFormRef = ref<FormInstance>()
const rebindSaving = ref(false)
const rebindForm = reactive({
  tenantId: undefined as number | undefined,
  inviteCode: '',
  contactMobile: '',
  reason: ''
})
const rebindRules: FormRules = {
  tenantId: [{ required: true, message: '请输入租户编号', trigger: 'blur' }],
  inviteCode: [{ required: true, message: '请输入邀请码', trigger: 'blur' }],
  contactMobile: [
    { required: true, message: '请输入确认手机', trigger: 'blur' },
    { pattern: /^1\d{10}$/, message: '手机号格式不正确', trigger: 'blur' }
  ]
}

const listLoading = ref(false)
const list = ref<SaasDistApi.SaasDistRelationVO[]>([])
const total = ref(0)
const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  companyName: undefined as string | undefined,
  contactMobile: undefined as string | undefined,
  status: undefined as number | undefined,
  all: false
})

const inviteQrText = computed(() => {
  if (!profile.value?.inviteCode) return ''
  // 扫码直达落地表单（H5 history 路由）
  const mobileBase = (import.meta.env.VITE_MOBILE_H5_DOMAIN || 'http://127.0.0.1:8080').replace(/\/$/, '')
  return `${mobileBase}/pages/modules/saas-dist/lead/index?inviteCode=${encodeURIComponent(profile.value.inviteCode)}`
})

const loadProfile = async () => {
  profileLoading.value = true
  try {
    profile.value = await SaasDistApi.getSaasDistProfile()
  } finally {
    profileLoading.value = false
  }
}

const getList = async () => {
  listLoading.value = true
  try {
    const data = await SaasDistApi.getSaasDistRelationPage({ ...queryParams })
    list.value = data.list || []
    total.value = data.total || 0
  } finally {
    listLoading.value = false
  }
}

const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

const resetQuery = () => {
  queryParams.companyName = undefined
  queryParams.contactMobile = undefined
  queryParams.status = undefined
  queryParams.all = false
  handleQuery()
}

const resetPull = () => {
  pullForm.companyName = ''
  pullForm.contactName = ''
  pullForm.contactMobile = ''
  pullForm.remark = ''
  pullFormRef.value?.clearValidate()
}

const submitPull = async () => {
  await pullFormRef.value?.validate()
  pullSaving.value = true
  try {
    await SaasDistApi.pullSaasDistCustomer({ ...pullForm })
    message.success('拉取成功（同人同码已存在则更新原线索）')
    resetPull()
    await getList()
  } finally {
    pullSaving.value = false
  }
}

const submitRebind = async () => {
  await rebindFormRef.value?.validate()
  if (!rebindForm.tenantId) return
  rebindSaving.value = true
  try {
    await SaasDistApi.rebindSaasDist({
      tenantId: rebindForm.tenantId,
      inviteCode: rebindForm.inviteCode.trim(),
      contactMobile: rebindForm.contactMobile.trim(),
      reason: rebindForm.reason?.trim() || undefined
    })
    message.success('补绑成功，分销关系已成立')
    rebindForm.tenantId = undefined
    rebindForm.inviteCode = ''
    rebindForm.contactMobile = ''
    rebindForm.reason = ''
    rebindFormRef.value?.clearValidate()
    await getList()
  } finally {
    rebindSaving.value = false
  }
}

const copyInvite = async () => {
  if (!profile.value?.inviteCode) return
  await navigator.clipboard.writeText(profile.value.inviteCode)
  ElMessage.success('邀请码已复制')
}

const copyInviteLink = async () => {
  if (!inviteQrText.value) return
  await navigator.clipboard.writeText(inviteQrText.value)
  ElMessage.success('邀请链接已复制')
}

onMounted(async () => {
  await loadProfile()
  await getList()
})
</script>

<style scoped>
.invite-panel {
  text-align: center;
  padding: 4px 0 12px;
}
.invite-hint {
  font-size: 13px;
  color: var(--el-text-color-secondary);
  line-height: 1.5;
  margin: 0 8px 14px;
  text-align: left;
}
.invite-poster {
  display: flex;
  justify-content: center;
}
.invite-poster__stage {
  position: relative;
  width: 100%;
  max-width: 340px;
  aspect-ratio: 575 / 1024;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 14px 36px rgba(15, 70, 160, 0.28);
  background: #0b5fff;
}
.invite-poster__bg {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  user-select: none;
  pointer-events: none;
}
/* 白框实测约 left16.5% top37.3% w65% h36%；QR 内缩并避开顶部「长按识别」条 */
.invite-poster__qr {
  position: absolute;
  left: 23%;
  top: 41.2%;
  width: 54%;
  aspect-ratio: 1 / 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  overflow: hidden;
}
.invite-poster__qrcode {
  width: 100% !important;
  height: 100% !important;
}
.invite-poster__qrcode :deep(canvas),
.invite-poster__qrcode :deep(img) {
  width: 100% !important;
  height: 100% !important;
  display: block;
}
.invite-code-row {
  margin-top: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}
.invite-code-label {
  font-size: 13px;
  color: var(--el-text-color-secondary);
}
.invite-code {
  font-size: 22px;
  font-weight: 700;
  letter-spacing: 2px;
  color: var(--el-color-primary);
}
.invite-link-preview {
  margin: 10px 8px 0;
  padding: 8px 10px;
  font-size: 12px;
  line-height: 1.4;
  color: var(--el-text-color-secondary);
  word-break: break-all;
  text-align: left;
  background: var(--el-fill-color-light);
  border-radius: 6px;
}
.invite-actions {
  margin-top: 12px;
  display: flex;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
}
.pull-form {
  max-width: 560px;
}
</style>
